import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/core/actions/dropdown-menu";
import Badge from "@/components/core/badge";
import { Button } from "@/components/core/buttons/button";
import { useModal } from "@/components/core/modals/use-modal";
import { useDeleteContractInvoiceMutation, useMarkInvoiceAsPaidMutation } from "@/features/dashboard/api/queries/contract-invoice-query";
import contractInvoiceService from "@/features/dashboard/api/services/contract-invoice-service";
import type { ContractInvoice } from "@/features/dashboard/types/contract-invoice";
import { useAuth } from "@/hooks/use-auth";
import { formatDate } from "@/utils/format-date";
import { formatNumber } from "@/utils/format-number";
import {
	IconCheck,
	IconClock,
	IconCreditCard,
	IconDots,
	IconDownload,
	IconEdit,
	IconFileInvoice,
	IconFileTypePdf,
	IconFileWord,
	IconTrash,
} from "@tabler/icons-react";
import React from "react";
import type { FC } from "react";
import { EditInvoiceModal } from "./edit-invoice-modal";
import { InvoiceCollectionStatusModal } from "./invoice-collection-status-modal";

interface InvoiceRowProps {
	invoice: ContractInvoice;
}

export const InvoiceRow: FC<InvoiceRowProps> = ({ invoice }) => {
	const { hasRole } = useAuth();
	const modal = useModal();
	const markPaidMutation = useMarkInvoiceAsPaidMutation();
	const deleteMutation = useDeleteContractInvoiceMutation();

	const handleUpdateCollectionStatus = () => {
		const collectionModalId = modal.openModal({
			title: "Update Collection Status",
			description: `Update collection status for invoice ${invoice.invoice_number}`,
			size: "lg",
			component: (
				<InvoiceCollectionStatusModal
					invoice={invoice}
					onSuccess={() => {
						modal.closeModal(collectionModalId);
					}}
				/>
			),
		});
	};

	const handleMarkAsPaid = () => {
		markPaidMutation.mutate({
			invoiceId: invoice.id,
			data: { payment_method: "manual" },
		});
	};

	const handleEdit = () => {
		const editModal = modal.openModal({
			title: "Edit Invoice",
			description: `Edit details for invoice ${invoice.invoice_number}`,
			size: "lg",
			component: (
				<EditInvoiceModal
					invoice={invoice}
					onSuccess={() => {
						modal.closeModal(editModal);
					}}
				/>
			),
		});
	};

	const handleDelete = () => {
		modal.openModal({
			type: "confirm",
			variant: "red",
			title: "Delete Invoice",
			description: `Are you sure you want to delete invoice "${invoice.invoice_number}"? This action cannot be undone.`,
			onConfirm: async () => {
				deleteMutation.mutate(invoice.id);
			},
		});
	};

	const canDeleteInvoice = () => {
		// Admin can delete any invoice
		if (hasRole(["admin"])) return true;

		// Accounting and billing can delete uncollected invoices
		if (hasRole(["accounting", "billing"]) && invoice.collection_status !== "collected") return true;

		// Salespeople can delete their own uncollected invoices
		if (hasRole(["salesperson"]) && invoice.collection_status !== "collected") return true;

		return false;
	};

	const getStatusBadge = (status: string) => {
		const colorMap: Record<string, string> = {
			pending: "yellow",
			paid: "green",
			overdue: "red",
			cancelled: "gray",
		};
		return <Badge color={colorMap[status] || "default"}>{status}</Badge>;
	};

	const getCollectionStatusBadge = (status: string) => {
		const colorMap: Record<string, string> = {
			pending: "orange",
			partial: "blue",
			collected: "green",
		};
		return <Badge color={colorMap[status] || "default"}>{status}</Badge>;
	};

	return (
		<tr className="border-l-4 border-l-blue-200 bg-blue-50/30">
			<td className="px-2 text-sm text-gray-900 font-medium max-w-[160px]">
				<div className="flex items-center">
					<IconFileInvoice className="w-4 h-4 mr-2 text-blue-600" />
					{invoice.invoice_number}
				</div>
			</td>
			<td className="px-2 text-sm text-gray-500 max-w-[300px] break-words">{invoice.description || "Invoice"}</td>
			<td className="px-2 text-sm text-gray-900">₱{formatNumber(invoice.amount)}</td>
			<td className="px-2 text-sm text-gray-500">
				<div>
					<p>{formatDate(invoice.invoice_date, "PP")}</p>
					<p className="text-xs text-red-700">Due: {formatDate(invoice.due_date, "PP")}</p>
				</div>
			</td>
			<td className="px-2 text-sm text-gray-500">
				<div>
					<p>{formatDate(invoice.date_collected, "PP")}</p>
					{invoice.collection_receipt && <p className="text-xs text-blue-600">Receipt: {invoice.collection_receipt}</p>}
				</div>
			</td>
			<td className="px-2 text-sm text-gray-500">
				<div className="flex items-center justify-between">
					{getCollectionStatusBadge(invoice.collection_status)}

					{(hasRole(["admin", "accounting", "billing"]) || hasRole(["salesperson"])) && (
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button variant="ghost" size="sm" className="h-8 w-8 p-0">
									<span className="sr-only">Open invoice menu</span>
									<IconDots className="h-4 w-4" />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end">
								<DropdownMenuItem onClick={() => contractInvoiceService.downloadPdf(invoice.id)}>
									<IconFileTypePdf className="w-4 h-4 mr-2" />
									Download PDF
								</DropdownMenuItem>
								<DropdownMenuItem onClick={() => contractInvoiceService.downloadDocx(invoice.id)}>
									<IconFileWord className="w-4 h-4 mr-2" />
									Download DOCX
								</DropdownMenuItem>
								{hasRole(["admin", "accounting", "billing", "salesperson"]) && (
									<>
										<DropdownMenuSeparator />
										<DropdownMenuItem onClick={handleEdit}>
											<IconEdit className="w-4 h-4 mr-2" />
											Edit Invoice
										</DropdownMenuItem>
										<DropdownMenuSeparator />
										<DropdownMenuItem onClick={handleUpdateCollectionStatus}>
											{invoice.collection_status === "collected" ? (
												<>
													<IconClock className="w-4 h-4 mr-2" />
													Update Collection Status
												</>
											) : (
												<>
													<IconCheck className="w-4 h-4 mr-2" />
													{invoice.collection_status === "partial" ? "Record Payment" : "Mark as Collected"}
												</>
											)}
										</DropdownMenuItem>
										<DropdownMenuSeparator />
									</>
								)}
								{/* {invoice.status === 'pending' && (
									<DropdownMenuItem 
										onClick={handleMarkAsPaid}
										disabled={markPaidMutation.isPending}
									>
										<IconCreditCard className="w-4 h-4 mr-2" />
										Mark as Paid
									</DropdownMenuItem>
								)} */}
								{canDeleteInvoice() && (
									<>
										<DropdownMenuSeparator />
										<DropdownMenuItem
											onClick={handleDelete}
											// disabled={deleteMutation.isPending}
											className="text-red-600 focus:text-red-600"
										>
											<IconTrash className="w-4 h-4 mr-2" />
											Delete Invoice
										</DropdownMenuItem>
									</>
								)}
							</DropdownMenuContent>
						</DropdownMenu>
					)}
				</div>
			</td>
		</tr>
	);
};
